import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { differenceInMonths } from 'date-fns';

import iconDownload from 'img/arrow-download.svg';

import { SituationProps } from 'components/TableSearchOrder/interface';
import { Column, Row } from 'components/Layout/Grid';
import { ActionButton } from 'components/ModelButton';
import { Visibility } from 'components/Visibility';
import RadioInput from 'components/RadioInput';
import Text from 'components/Text';

import {
  calcDataFrom,
  calcDataTo,
  formatDateToISO_UTC,
  getMaxDateInSearchFilter,
  getMinDateInSearchFilter,
  validateDatePeriodFollowUp
} from 'utils/Date';
import { DateOutputStr } from 'utils/DateOutput';
import { Period } from 'utils/Date/interface';
import { restartHubConnection } from 'utils/HubNotification';

import { FollowUpSliderProps, SituationObjProps } from './interface';

import DomainSettingService from 'services/domainSetting.service';
import OrderServices from 'services/order.service';

import { FollowUpEnum } from './model';
import { TicketReasonEnum } from 'models/Order';

import { useHubNotification } from 'hooks/useHubNotification';

const FollowUpSlider: React.FC<FollowUpSliderProps> = (props) => {
  const { showTypeDateFilter = false, typeFollowUp } = props;
  const dispatch = useDispatch();

  const _domainSettingService = new DomainSettingService();

  const randomTableSearchId = `${Math.random() * 1000}`;
  const tableOrderBy = "id desc";

  let minMonth = 3;

  if (typeFollowUp === FollowUpEnum.DELIVERED)
    minMonth = 1;

  const [newData, setNewData] = useState('');

  const [filterFollowUp, setFilterFollowUp] = useState<Period>({
    from: DateOutputStr(getMinDateInSearchFilter(minMonth).toISOString(), 'yyyy-MM-dd'),
    to: DateOutputStr(getMaxDateInSearchFilter().toISOString(), 'yyyy-MM-dd'),
    filterDate: "BillingDate",
    status: "",
    ticketReason: ""
  });

  const [allSituationOption, setAllSituationOption] = useState<Array<SituationObjProps>>([]);

  useEffect(() => {
    fetchDomainSettingsTrackingTypes();
  }, []);

  const fetchDomainSettingsTrackingTypes = async () => {
    try {
      const result = await _domainSettingService.get(undefined, undefined, 'value asc', {
        and: [
          { identifier: { eq: 'GroupTrackingTypes' } },
          { enabled: { eq: true } }
        ],
      });

      if (result && result.data && result.data.length > 0) {
        const { value } = result.data[0];
        const situationArray = JSON.parse(value || '');

        const allObjSituationOption: Array<SituationObjProps> = situationArray.map((item: SituationProps) => ({
          group: item.group,
          status: item.status,
        }));

        setAllSituationOption(allObjSituationOption);
      }
    } catch (error) {
      console.log('Erro ao obter as configurações do domínio:', error);
    }
  };

  function conditionByExportData() {

    const isValidDate = validateDateBySituation();

    if (!isValidDate) return false;

    return exportData();
  };

  const validateDateBySituation = (): boolean => {
    if (typeFollowUp === FollowUpEnum.NOT_DELIVERED && !filterFollowUp.from)
      return true;

    const dateFilterStart = filterFollowUp.from || DateOutputStr(getMinDateInSearchFilter(minMonth).toISOString(), 'yyyy-MM-dd');
    const dateFilterEnd = filterFollowUp.to || getMaxDateInSearchFilter();

    const startDate = new Date(dateFilterStart);
    const endDate = new Date(dateFilterEnd);

    const startDateUTC = formatDateToISO_UTC(startDate);
    const endDateUTC = formatDateToISO_UTC(endDate);

    const diffInMonth = differenceInMonths(endDateUTC, startDateUTC);

    return validateDatePeriodFollowUp(diffInMonth, minMonth);
  };

  const handleDateFirst = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterFollowUp({ ...filterFollowUp, from: e.target.value });
    setNewData('end');
  };

  const handleDateEnd = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterFollowUp({ ...filterFollowUp, to: e.target.value });
    setNewData('start');
  };

  const exportData = async () => {

    const dateFilterStart = filterFollowUp.from || DateOutputStr(getMinDateInSearchFilter(minMonth).toISOString(), 'yyyy-MM-dd');
    const dateFilterEnd = filterFollowUp.to || DateOutputStr(getMaxDateInSearchFilter().toISOString(), 'yyyy-MM-dd');

    let situationStatus = "";
    let ticketReason = "";

    if (typeFollowUp === FollowUpEnum.DELIVERED) {
      const filteredItems = allSituationOption.filter((item: any) => item.group === "Entregue");
      situationStatus = filteredItems.length > 0 ? filteredItems[0].status : "";
      ticketReason = TicketReasonEnum.FOLLOWUP_EXTRACTION_DELIVERY;
    }

    if (typeFollowUp === FollowUpEnum.NOT_DELIVERED) {
      const filteredItems = allSituationOption.filter((item: any) => item.group === "Não Entregue");
      situationStatus = filteredItems.length > 0 ? filteredItems[0].status : "";
      ticketReason = TicketReasonEnum.FOLLOWUP_EXTRACTION_NON_DELIVERY;
    }

    const newFilter = {
      startPeriod: `${dateFilterStart}T00:00:00.000Z`,
      lastPeriod: `${dateFilterEnd}T23:59:59.999Z`,
      status: situationStatus,
      ticketReason: ticketReason
    };

    await restartHubConnection(useHubNotification());

    await new OrderServices().downloadExportData(
      { ...newFilter }, tableOrderBy
    );

    dispatch({ type: 'CLOSE_SLIDER' });
  };

  const handleSelectDateType = (value: string) => {
    setFilterFollowUp({ ...filterFollowUp, filterDate: value });
  };

  return (
    <Row
      id={randomTableSearchId}
      tabIndex={0}
      className="d-flex"
    >
      <Row
        className="align-center flex-md-column align-md-none flex-wrap justify-md-none bg-white py-3"
        style={{ borderRadius: '10px 10px 0px 0px' }}
      >
        <Column className="mr-2 mr-md-2 w-md-100 ajustment-filter">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
            Período
          </Text>
          <Row className="flex-1 align-center grid-gap-3 flex-wrap">
            <Row className="flex-1 align-center">
              <input
                className="flex-1"
                value={filterFollowUp.from}
                onChange={handleDateFirst}
                type="date"
                max={filterFollowUp.to}
                placeholder="Início"
                onBlur={() => calcDataTo(filterFollowUp, newData, setFilterFollowUp, minMonth)}
                id="date_first_order"
              />
              <Text className="mx-1 color-blue-gray" size={12} weight="bold">
                a
              </Text>
              <input
                value={filterFollowUp.to}
                onChange={handleDateEnd}
                className="flex-1"
                type="date"
                min={filterFollowUp.from}
                placeholder="Fim"
                onBlur={() => calcDataFrom(filterFollowUp, newData, setFilterFollowUp, minMonth)}
                id="date_end_order"
              />

            </Row>

            <Visibility visible={showTypeDateFilter}>
              <Row className="flex-1 align-center grid-gap-3">
                <RadioInput
                  id="BILLING_DATE"
                  onClick={() => handleSelectDateType("BillingDate")}
                  checked={filterFollowUp.filterDate === 'BillingDate'}
                >
                  <Text className="color-blue-gray">Data do faturamento</Text>
                </RadioInput>

                <RadioInput
                  id="INVOICE_DELIVERY_DATE"
                  onClick={() => handleSelectDateType("InvoiceDeliveryDate")}
                  checked={filterFollowUp.filterDate === 'InvoiceDeliveryDate'}
                >
                  <Text className="color-blue-gray">Data de entrega</Text>
                </RadioInput>
              </Row>
            </Visibility>

            <Row className="flex-1 align-center">
              <ActionButton
                img={iconDownload}
                textButton="Exportar Dados"
                action={conditionByExportData}
              />
            </Row>
          </Row>

        </Column>

      </Row>
    </Row>
  );
};

export default FollowUpSlider;